import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
    GetCalculationVersionsInput,
    GetCalculationVersionsOutput,
    GetPlayTypesInput,
    GetRoyaltyManagerExecutionRequestDetailsByIdInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Icon from '@amzn/meridian/icon';
import Popover, { PopoverHeader } from '@amzn/meridian/popover';
import Row from '@amzn/meridian/row';
import Toggle from '@amzn/meridian/toggle';

import { GridApi } from 'ag-grid-community';
import { useHistory, useParams } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { FilterInput } from 'src/components/FilterInput';
import {
    convertRowToUSD,
    createEntityIdMapping,
    processCalculationVersions,
    processRawCalculationVersions,
    processRoyaltyVersions
} from 'src/components/RoyaltyManager/utils/executionRequestDataProcessing';
import {
    downloadGridDataAsCSV,
    downloadRoyaltyVersionsCSV
} from 'src/components/RoyaltyManager/utils/royaltiesDownload';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { ExecutionRequestDetailsRowData } from 'src/types/IExecutionRequestDetail';

import { DownloadDropdown } from './DownloadDropdown';
import './ExecutionRequestDetails.scss';
import { getExecutionRequestDetailsColDefs } from './ExecutionRequestDetailsColDefs';
import { getRawCalculationColDefs } from './RawCalculationColDefs';
import { RoyaltyDetailsModal } from './RoyaltyDetailsModal';

export const ExecutionRequestDetails: React.FC = () => {
    const params: { executionRequestId: string } = useParams();
    const executionRequestId = parseInt(params.executionRequestId);
    const history = useHistory();
    const { metadata, setTopAlertMsg, entities } = useContext(AppContext);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [rowData, setRowData] = useState<ExecutionRequestDetailsRowData[]>([]);
    const [rawRowData, setRawRowData] = useState<any[]>([]);
    const [filterText, setFilterText] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingType, setDownloadingType] = useState<
        'calculation' | 'royalty' | 'raw-calculation' | 'raw-royalty' | null
    >(null);
    const [royaltyData, setRoyaltyData] = useState<any[]>([]);
    const [isRoyaltyDataLoaded, setIsRoyaltyDataLoaded] = useState(false);
    const [showRoyaltyModal, setShowRoyaltyModal] = useState(false);
    const [selectedRowRoyalties, setSelectedRowRoyalties] = useState<any[]>([]);
    const [selectedRowMonth, setSelectedRowMonth] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [showNonZeroOnly, setShowNonZeroOnly] = useState(true);
    const [showRawOutput, setShowRawOutput] = useState(false);
    const [playTypeMapping, setPlayTypeMapping] = useState<{ [key: number]: string }>({});
    const [comparisonGridApi, setComparisonGridApi] = useState<GridApi>();
    const [rawGridApi, setRawGridApi] = useState<GridApi>();
    const [convertToUSD, setConvertToUSD] = useState(false);
    const [conversionRates, setConversionRates] = useState<Map<string, Map<string, number>>>(new Map());
    const [infoPopoverOpen, setInfoPopoverOpen] = useState(false);
    const infoButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        contractConfigurationClient
            .getRoyaltyManagerExecutionRequestDetailsById(
                GetRoyaltyManagerExecutionRequestDetailsByIdInput.clone({
                    executionRequestId: executionRequestId
                })
            )
            .toPromise()
            .then((response) => {
                const details = (response as any).executionRequestDetails;
                if (details?.description) {
                    setDescription(details.description);
                }
            })
            .catch((error) => {
                console.error('Error fetching execution request details:', error);
                setTopAlertMsg(
                    error?.receivedException?.message || error?.message || 'Failed to load execution request details.'
                );
            });
    }, [executionRequestId]);

    // Fetch play types mapping
    useEffect(() => {
        contractConfigurationClient
            .getPlayTypes(GetPlayTypesInput.clone({}))
            .toPromise()
            .then((response) => {
                const mapping: { [key: number]: string } = {};
                response.playTypes?.forEach((playType) => {
                    if (playType.playTypeId && playType.playTypeName) {
                        mapping[playType.playTypeId] = playType.playTypeName;
                    }
                });
                setPlayTypeMapping(mapping);
            })
            .catch((error) => {
                console.error('Error fetching play types:', error);
            });
    }, []);

    // Preload royalty data in background
    useEffect(() => {
        if (Object.keys(playTypeMapping).length === 0 || !entities) return;

        contractConfigurationClient
            .getRoyaltyVersions({
                executionRequestId: executionRequestId
            })
            .toPromise()
            .then((response) => {
                const entityIdMapping = createEntityIdMapping(entities);
                const processedRoyaltyData = processRoyaltyVersions(
                    response.royaltyVersions,
                    entityIdMapping,
                    metadata,
                    playTypeMapping
                );
                setRoyaltyData(processedRoyaltyData);
                setIsRoyaltyDataLoaded(true);
            })
            .catch((error) => {
                console.error('Error preloading royalty data:', error);
                setTopAlertMsg(error?.receivedException?.message || error?.message || 'Failed to load royalty data.');
                setIsRoyaltyDataLoaded(true);
            });
    }, [executionRequestId, metadata, setTopAlertMsg, playTypeMapping, entities]);

    const showRoyaltyDetails = useCallback(
        (calculationRow: ExecutionRequestDetailsRowData) => {
            const serviceId = Object.keys(metadata.serviceMapping).find(
                (id) => metadata.serviceMapping[parseInt(id)].serviceName === calculationRow.service
            );

            const matchingRoyalties = royaltyData.filter(
                (royalty) =>
                    royalty.serviceId.toString() === serviceId &&
                    royalty.territoryCode === calculationRow.territory &&
                    royalty.entity === calculationRow.entity &&
                    royalty.month === calculationRow.month &&
                    royalty.royaltyVendorCode === calculationRow.royaltyVendorCode
            );

            setSelectedRowRoyalties(matchingRoyalties);
            setSelectedRowMonth(calculationRow.month);
            setShowRoyaltyModal(true);
        },
        [metadata.serviceMapping, royaltyData]
    );

    const filteredRowData = useMemo(() => {
        return showNonZeroOnly
            ? rowData.filter((row) => row.beforeAmount != null && row.beforeAmount !== row.afterAmount)
            : rowData;
    }, [rowData, showNonZeroOnly]);

    const fetchConversionRates = useCallback(async () => {
        const currentData = showRawOutput ? rawRowData : filteredRowData;
        const months = new Set<string>();
        const currencies = new Set<string>();

        currentData.forEach((row) => {
            if (row.month) months.add(row.month);
            if (row.afterCurrency && row.afterCurrency !== 'USD') currencies.add(row.afterCurrency);
            if (row.beforeCurrency && row.beforeCurrency !== 'USD') currencies.add(row.beforeCurrency);
            if (row.currency && row.currency !== 'USD') currencies.add(row.currency);
        });

        if (months.size === 0 || currencies.size === 0) return;

        const newRates = new Map<string, Map<string, number>>();

        for (const month of Array.from(months)) {
            try {
                const response = await contractConfigurationClient
                    .getCurrencyConversionRates({ month, toCurrency: 'USD' })
                    .toPromise();

                const monthRates = new Map<string, number>();
                response.currencyConversionRates?.forEach((rate: any) => {
                    if (rate.fromCurrency && rate.exchangeRate) {
                        monthRates.set(rate.fromCurrency, parseFloat(rate.exchangeRate));
                    }
                });
                newRates.set(month, monthRates);
            } catch (error) {
                console.error(`Error fetching conversion rates for ${month}:`, error);
                setTopAlertMsg(`Failed to fetch conversion rates for ${month}`);
            }
        }

        setConversionRates(newRates);
    }, [showRawOutput, rawRowData, filteredRowData, setTopAlertMsg]);

    useEffect(() => {
        if (convertToUSD && conversionRates.size === 0) {
            fetchConversionRates();
        }
    }, [convertToUSD, conversionRates.size, fetchConversionRates]);

    const displayRowData = useMemo(() => {
        const baseData = showRawOutput ? rawRowData : filteredRowData;
        if (!convertToUSD || conversionRates.size === 0) return baseData;

        return baseData.map((row) => convertRowToUSD(row, conversionRates));
    }, [showRawOutput, rawRowData, filteredRowData, convertToUSD, conversionRates]);

    useEffect(() => {
        if (!entities) return;

        contractConfigurationClient
            .getCalculationVersions(
                GetCalculationVersionsInput.clone({
                    executionRequestId: executionRequestId
                })
            )
            .toPromise()
            .then((calculationVersionsResponse: GetCalculationVersionsOutput) => {
                const entityIdMapping = createEntityIdMapping(entities);
                const incomingRowData = processCalculationVersions(
                    calculationVersionsResponse.calculationVersions,
                    entityIdMapping,
                    metadata
                );
                setRowData(incomingRowData);

                const rawData = processRawCalculationVersions(
                    calculationVersionsResponse.calculationVersions,
                    entityIdMapping,
                    metadata
                );
                setRawRowData(rawData);
            })
            .catch((error: UnknownCoralError) =>
                setTopAlertMsg(error?.receivedException?.message || error?.message || 'An error occurred')
            )
            .finally(() => setIsDataLoaded(true));
    }, [executionRequestId, metadata, setTopAlertMsg, entities]);

    const handleDownloadCalculation = () => {
        try {
            setIsDownloading(true);
            setDownloadingType('calculation');
            const filename = `calculation-versions-comparison-${executionRequestId}${convertToUSD ? '-USD' : ''}.csv`;
            downloadGridDataAsCSV(comparisonGridApi, filename);
        } catch (error) {
            console.error('Error downloading calculation versions:', error);
            setTopAlertMsg((error as any)?.message || 'Failed to download calculation versions.');
        } finally {
            setIsDownloading(false);
            setDownloadingType(null);
            setShowDropdown(false);
        }
    };

    const handleDownloadRoyalty = async () => {
        setIsDownloading(true);
        setDownloadingType('royalty');
        try {
            await downloadRoyaltyVersionsCSV(
                executionRequestId,
                contractConfigurationClient,
                metadata,
                playTypeMapping,
                convertToUSD,
                conversionRates
            );
        } catch (error) {
            console.error('Error downloading royalty versions:', error);
            setTopAlertMsg(
                (error as any)?.receivedException?.message ||
                    (error as any)?.message ||
                    'Failed to download royalty versions.'
            );
        } finally {
            setIsDownloading(false);
            setDownloadingType(null);
            setShowDropdown(false);
        }
    };

    const handleDownloadRawCalculation = () => {
        try {
            setIsDownloading(true);
            setDownloadingType('raw-calculation');
            const filename = `calculation-versions-raw-${executionRequestId}${convertToUSD ? '-USD' : ''}.csv`;
            downloadGridDataAsCSV(rawGridApi, filename);
        } catch (error) {
            console.error('Error downloading raw calculation versions:', error);
            setTopAlertMsg((error as any)?.message || 'Failed to download raw calculation versions.');
        } finally {
            setIsDownloading(false);
            setDownloadingType(null);
            setShowDropdown(false);
        }
    };

    const handleDownloadRawRoyalty = async () => {
        setIsDownloading(true);
        setDownloadingType('raw-royalty');
        try {
            await downloadRoyaltyVersionsCSV(
                executionRequestId,
                contractConfigurationClient,
                metadata,
                playTypeMapping,
                convertToUSD,
                conversionRates,
                true
            );
        } catch (error) {
            console.error('Error downloading raw royalty versions:', error);
            setTopAlertMsg(
                (error as any)?.receivedException?.message ||
                    (error as any)?.message ||
                    'Failed to download raw royalty versions.'
            );
        } finally {
            setIsDownloading(false);
            setDownloadingType(null);
            setShowDropdown(false);
        }
    };

    return (
        <div className="execution-request-details-container">
            <Column
                className="execution-request-details-content"
                spacing="300"
                width="100%"
                height="100%"
                minHeight={480}
            >
                <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                    <span
                        style={{ color: '#0073bb', cursor: 'pointer' }}
                        onClick={() => history.push(ROUTES.ROYALTY_MANAGER.INDEX)}
                    >
                        Royalty Manager
                    </span>
                    <span style={{ margin: '0 8px', color: '#666' }}>&gt;</span>
                    <span style={{ color: '#666' }}>Execution Request Details</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Heading level={2}>Execution request: {executionRequestId}</Heading>
                        {description && <div style={{ marginTop: '8px', color: '#666' }}>{description}</div>}
                    </div>
                    <DownloadDropdown
                        showDropdown={showDropdown}
                        setShowDropdown={setShowDropdown}
                        isDataLoaded={isDataLoaded}
                        rowDataLength={rowData.length}
                        isDownloading={isDownloading}
                        downloadingType={downloadingType}
                        onDownloadCalculation={handleDownloadCalculation}
                        onDownloadRoyalty={handleDownloadRoyalty}
                        onDownloadRawCalculation={handleDownloadRawCalculation}
                        onDownloadRawRoyalty={handleDownloadRawRoyalty}
                    />
                </div>
                <Box type="fill" spacingInset="400">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Row alignmentVertical="center" spacing="400">
                            <FilterInput filterText={filterText} setFilterText={setFilterText} />
                            <Row alignmentVertical="center" spacing="200">
                                <Toggle checked={showNonZeroOnly} onChange={(checked) => setShowNonZeroOnly(checked)} />
                                <span>Show non-zero deltas only</span>
                            </Row>
                            <Row alignmentVertical="center" spacing="100">
                                <Toggle checked={convertToUSD} onChange={(checked) => setConvertToUSD(checked)} />
                                <span>Convert to USD</span>
                                <Button
                                    ref={infoButtonRef}
                                    type="link"
                                    size="small"
                                    circular={true}
                                    onClick={() => setInfoPopoverOpen(true)}
                                >
                                    <Icon tokens={infoKnockoutTokens} />
                                </Button>
                                <Popover
                                    anchorNode={infoButtonRef.current as any}
                                    open={infoPopoverOpen}
                                    onClose={() => setInfoPopoverOpen(false)}
                                    position="end"
                                    alignment="top"
                                    maxWidth="250px"
                                >
                                    <PopoverHeader closeLabel="Close">Exchange Rate Info</PopoverHeader>
                                    <div style={{ padding: '4px 8px 12px 8px', fontSize: '12px', lineHeight: '1.4' }}>
                                        Exchange rate used is taken from third last day of rerun month.
                                        <br />
                                        <br />
                                        Toggling this on will also convert royalties to USD.
                                    </div>
                                </Popover>
                            </Row>
                            {convertToUSD && !showRawOutput && (
                                <Row alignmentVertical="center" spacing="200">
                                    <span style={{ fontWeight: 'bold' }}>
                                        Total Impact in USD: $
                                        {displayRowData
                                            .reduce((sum, row) => {
                                                const delta = (row.afterAmount || 0) - (row.beforeAmount || 0);
                                                return sum + delta;
                                            }, 0)
                                            .toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                    </span>
                                </Row>
                            )}
                        </Row>
                        <ButtonGroup
                            value={showRawOutput ? 'raw' : 'compare'}
                            onChange={(value) => setShowRawOutput(value === 'raw')}
                        >
                            <ButtonOption value="compare">Compare</ButtonOption>
                            <ButtonOption value="raw">Raw</ButtonOption>
                        </ButtonGroup>
                    </div>
                </Box>
                <div className="execution-request-details-grid">
                    <div style={{ display: showRawOutput ? 'none' : 'block', height: '100%', width: '100%' }}>
                        <BaseAgGrid
                            columnDefs={getExecutionRequestDetailsColDefs(showRoyaltyDetails, isRoyaltyDataLoaded)}
                            rowData={displayRowData}
                            filterText={filterText}
                            setAgGridApi={setComparisonGridApi}
                            loading={!isDataLoaded}
                            defaultColDef={{ wrapHeaderText: true, autoHeaderHeight: true }}
                        />
                    </div>
                    <div style={{ display: showRawOutput ? 'block' : 'none', height: '100%', width: '100%' }}>
                        <BaseAgGrid
                            columnDefs={getRawCalculationColDefs(showRoyaltyDetails, isRoyaltyDataLoaded)}
                            rowData={displayRowData}
                            filterText={filterText}
                            setAgGridApi={setRawGridApi}
                            loading={!isDataLoaded}
                            defaultColDef={{ wrapHeaderText: true, autoHeaderHeight: true }}
                        />
                    </div>
                </div>
            </Column>

            <RoyaltyDetailsModal
                open={showRoyaltyModal}
                onClose={() => setShowRoyaltyModal(false)}
                selectedRowRoyalties={selectedRowRoyalties}
                showRawOutput={showRawOutput}
                convertToUSD={convertToUSD}
                conversionRates={conversionRates}
                month={selectedRowMonth}
            />
        </div>
    );
};
